import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BROKERS as SEED_BROKERS } from "@/data/brokers";
import { adminBulkUpsertBrokers, adminCreateBroker, adminDeleteBroker, fetchCustomBrokers } from "@/lib/adminApi";

const LOCAL_KEY = "fbs_custom_brokers";
const BrokersContext = createContext(null);

const readLocal = () => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocal = (brokers) => {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(brokers));
};

const mergeBrokers = (custom) => {
  const bySlug = new Map();
  SEED_BROKERS.forEach((b) => bySlug.set(b.slug, b));
  custom.forEach((b) => bySlug.set(b.slug, b));
  return Array.from(bySlug.values());
};

export function BrokersProvider({ children }) {
  const [custom, setCustom] = useState(() => readLocal());

  const refreshCustom = useCallback(async () => {
    try {
      const remote = await fetchCustomBrokers();
      if (Array.isArray(remote)) {
        setCustom(remote);
        writeLocal(remote);
        return remote;
      }
    } catch {
      /* keep local cache when API is offline */
    }
    return readLocal();
  }, []);

  useEffect(() => {
    refreshCustom();
  }, [refreshCustom]);

  const brokers = useMemo(() => mergeBrokers(custom), [custom]);

  const getBroker = useCallback((slug) => brokers.find((b) => b.slug === slug), [brokers]);

  const upsertCustomBroker = useCallback(async (broker) => {
    const saved = await adminCreateBroker(broker);
    const nextBroker = saved?.broker || broker;
    setCustom((prev) => {
      const next = [...prev.filter((b) => b.slug !== nextBroker.slug), nextBroker];
      writeLocal(next);
      return next;
    });
    return nextBroker;
  }, []);

  const removeCustomBroker = useCallback(async (slug) => {
    await adminDeleteBroker(slug);
    setCustom((prev) => {
      const next = prev.filter((b) => b.slug !== slug);
      writeLocal(next);
      return next;
    });
  }, []);

  const bulkUpsertBrokers = useCallback(async (list) => {
    const res = await adminBulkUpsertBrokers(list);
    setCustom((prev) => {
      const bySlug = new Map(prev.map((b) => [b.slug, b]));
      list.forEach((b) => bySlug.set(b.slug, b));
      const next = Array.from(bySlug.values());
      writeLocal(next);
      return next;
    });
    return res;
  }, []);

  const value = useMemo(
    () => ({
      brokers,
      customBrokers: custom,
      seedCount: SEED_BROKERS.length,
      getBroker,
      upsertCustomBroker,
      removeCustomBroker,
      bulkUpsertBrokers,
      refreshCustom,
    }),
    [brokers, custom, getBroker, upsertCustomBroker, removeCustomBroker, bulkUpsertBrokers, refreshCustom],
  );

  return <BrokersContext.Provider value={value}>{children}</BrokersContext.Provider>;
}

export function useBrokers() {
  const ctx = useContext(BrokersContext);
  if (!ctx) throw new Error("useBrokers must be used within BrokersProvider");
  return ctx;
}
